import SectionTitle from "../layout/SectionTitle";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useTranslation } from 'react-i18next';
import { FaQuoteLeft, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import Slider from 'react-slick';
import styled from 'styled-components';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// Composant de flèche personnalisé pour la navigation
const Arrow = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  cursor: pointer;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(5px);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  color: #1e3a8a;
  font-size: 16px;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  
  &:hover {
    background-color: #1e3a8a;
    color: white;
    box-shadow: 0 6px 24px rgba(30, 58, 138, 0.2);
  }
  
  &.slick-prev {
    left: -22px;
  }
  
  &.slick-next {
    right: -22px;
  }
  
  @media (max-width: 768px) {
    display: none !important;
  }
`;

// Conteneur du slider avec styles
const SliderContainer = styled.div`
  .slick-slide > div {
    padding: 0 16px;
    outline: none;
    height: 100%;
  }
  
  .slick-slide {
    height: auto;
  }
  
  .slick-track {
    display: flex;
    align-items: stretch;
    padding: 20px 0 40px;
  }

  .slick-dots {
    bottom: -5px;
    
    li {
      margin: 0 6px;
      
      button:before {
        font-size: 0;
        width: 24px;
        height: 3px;
        background-color: rgba(30, 58, 138, 0.2);
        border-radius: 4px;
        opacity: 1;
        content: '';
        transition: all 0.3s ease;
      }
      
      &.slick-active button:before {
        background-color: #1e3a8a;
        width: 32px;
      }
    }
  }
  
  .testimonial-card {
    height: 100%;
    transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
    
    &:hover {
      transform: translateY(-5px);
    }
  }
`;

// Configuration du carrousel
const settings = {
  dots: true,
  infinite: true,
  speed: 700,
  slidesToShow: 3,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 6000,
  pauseOnHover: true,
  nextArrow: <Arrow className="slick-next"><FaChevronRight /></Arrow>,
  prevArrow: <Arrow className="slick-prev"><FaChevronLeft /></Arrow>,
  swipe: true,
  swipeToSlide: true,
  touchMove: true,
  draggable: true,
  cssEase: "cubic-bezier(0.4, 0, 0.2, 1)",
  responsive: [
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1,
      }
    },
    {
      breakpoint: 768,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
        dots: true
      }
    }
  ]
};

export default function Temoignages() {
  const { t } = useTranslation();
  const [sectionRef, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  // Liste des clés des témoignages
  const testimonialKeys = [1, 2, 3, 4, 5, 6, 7].map(num => `client${num}`);

  return (
    <section className="py-8 bg-socabeg-gray-light" ref={sectionRef} id="temoignages">
      <motion.div 
        className="container mx-auto px-4"
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ duration: 0.6 }}
      >
        <div className="mb-10">
          <SectionTitle
            overline={t('home.testimonials.title')}
            highlight={t('home.testimonials.highlight')}
            showLogo={true}
            align="left"
          />
        </div>

        <motion.div 
          className="min-h-[350px]"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          <SliderContainer>
            <Slider {...settings}>
              {testimonialKeys.map((key, index) => (
                <div key={index} className="h-full">
                  <div className="testimonial-card bg-white rounded-2xl shadow-lg h-full flex flex-col min-h-[260px] relative overflow-hidden">
                    {/* Fond décoratif */}
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-socabeg-blue to-socabeg-blue/50"></div>
                    
                    {/* Contenu */}
                    <div className="p-7 flex flex-col h-full">
                      {/* Citation */}
                      <div className="relative">
                        <div className="absolute -left-1 -top-1 opacity-10">
                          <FaQuoteLeft className="text-socabeg-blue text-3xl" />
                        </div>
                        <p className="text-socabeg-dark-gray mb-8 flex-grow text-base leading-relaxed pl-3 pt-2 text-justify italic">
                          {t(`home.testimonials.${key}.quote`)}
                        </p>
                      </div>
                      
                      {/* Informations client */}
                      <div className="mt-auto pt-4 flex items-center">
                        <div className="w-10 h-10 rounded-full bg-socabeg-blue/10 flex items-center justify-center text-socabeg-blue mr-3">
                          {t(`home.testimonials.${key}.name`).charAt(0)}
                        </div>
                        <div>
                          <h3 className="font-semibold text-socabeg-blue">{t(`home.testimonials.${key}.name`)}</h3>
                          <p className="text-gray-500 text-sm">{t(`home.testimonials.${key}.position`)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          </SliderContainer>
        </motion.div>
      </motion.div>
    </section>
  );
}
